import { SearchOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { formatDisplayName } from 'library/helpers/utility';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { Badge, Button, Checkbox, Divider, Flex, Input, Select, Space } from 'zui/Antd';

const FilterPeopleSelect = ({ selectedPeople, setSelectedPeople, sitePeople }) => {
	// ✅ theme tokens
	const {
		token: { paddingXS, padding, borderRadiusOuter, colorBorder, colorTextDescription, colorPrimary }
	} = theme.useToken();

	// ✅ full list from Redux
	const allPeople = useSelector(selectPeople);

	// ✅ restrict if sitePeople provided
	const people = useMemo(() => {
		if (!sitePeople || sitePeople.length === 0) return allPeople;
		const allowed = new Set(sitePeople.map((p) => p.personID));
		return allPeople.filter((p) => allowed.has(p.personID));
	}, [allPeople, sitePeople]);

	// ✅ search state
	const [searchText, setSearchText] = useState('');

	// 🔍 filter by search
	const filteredPeople = useMemo(() => {
		return people.filter((p) => formatDisplayName(p).displayName.toLowerCase().includes(searchText.toLowerCase()));
	}, [people, searchText]);

	// 🔃 sort selected first
	const selected = filteredPeople.filter((p) => selectedPeople.includes(p.personID));
	const notSelected = filteredPeople.filter((p) => !selectedPeople.includes(p.personID));

	// ✅ insert divider if needed
	const sortedPeople =
		selected.length > 0 ? [...selected, { personID: 'divider', isDivider: true }, ...notSelected] : notSelected;

	// ✅ toggle selection
	const handleCheckboxChange = (personID) => {
		const updated = selectedPeople.includes(personID)
			? selectedPeople.filter((id) => id !== personID)
			: [...selectedPeople, personID];
		setSelectedPeople(updated);
	};

	// ✅ build dropdown options
	const options = sortedPeople.map((person) => {
		const isDiv = person.isDivider;
		const name = isDiv ? '' : formatDisplayName(person).displayName;
		return {
			label: name,
			value: person.personID,
			disabled: isDiv,
			component: isDiv ? (
				<Divider style={{ margin: 0, width: 276, borderColor: colorBorder }} />
			) : (
				<Checkbox checked={selectedPeople.includes(person.personID)}>{name}</Checkbox>
			),
			onClick: isDiv ? undefined : () => handleCheckboxChange(person.personID)
		};
	});

	return (
		<Select
			mode='multiple'
			value={selectedPeople}
			onChange={setSelectedPeople}
			style={{ minWidth: 100 }}
			dropdownStyle={{ width: 276 }}
			placeholder='People'
			options={options}
			showSearch={false}
			prefix={
				selectedPeople.length > 0 && (
					<Badge
						count={selectedPeople.length}
						style={{
							backgroundColor: colorPrimary,
							borderRadius: borderRadiusOuter,
							marginRight: paddingXS
						}}
					/>
				)
			}
			dropdownRender={(menu) => (
				<Flex vertical style={{ borderRadius: borderRadiusOuter }}>
					<Flex justify='space-between' align='center' style={{ padding }}>
						<Input
							placeholder='Search People'
							prefix={<SearchOutlined style={{ color: colorTextDescription }} />}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							variant='borderless'
						/>
						<Button
							type='text'
							onClick={() => {
								setSelectedPeople([]);
								setSearchText('');
							}}
							style={{ padding: 0, textDecoration: 'underline' }}
						>
							Clear
						</Button>
					</Flex>
					{menu}
				</Flex>
			)}
			optionRender={(opt) => <Space>{opt.data.component}</Space>}
			tagRender={({ value }) => {
				const person = allPeople.find((p) => p.personID === value);
				return person ? formatDisplayName(person).displayName : null;
			}}
			maxTagCount={1}
			menuItemSelectedIcon={null}
		/>
	);
};

export default FilterPeopleSelect;
